/**
 * The add-players panel's PERSISTENT global search (SE-5490).
 *
 * Search is a field, not a tab, and it deliberately BYPASSES tab scoping: while
 * a query is active the panel shows any player the org can see, regardless of
 * which source tab is selected and regardless of which tabs the pipeline's
 * motion offers. That is the point of the affordance — the tabs are curated
 * pools, and search is the escape hatch when the player you want is in none of
 * them.
 *
 * It reuses the existing global player search rather than adding a third search
 * path: `PlayerSearchSearchPlayers`. Until SE-6948 this was the count-free
 * Lite variant — `totalCount` re-runs the whole filter with no LIMIT and
 * accounts for roughly 41% of that operation's SQL time, so a surface that
 * renders no count must not ask for one. PC2's results caption ("ALL PLAYERS ·
 * N MATCHES") now DISPLAYS the total, which is exactly the opt-in the SE-5776
 * rule describes, so the counted document is the honest one here.
 *
 * The read stays gated at three characters: a query shorter than that cannot
 * use the backend's trigram index and degrades to a full scan.
 *
 * ## The results paginate to the reported total (SE-7100)
 *
 * PC2's caption states the connection's real `totalCount`, so the list must
 * be able to reach it: the hook tracks the connection's `pageInfo` through
 * `useInfiniteScroll` (the same relay pattern the filtered-results body and
 * the Search Player modal use) and exposes `loadMore`/`hasMore` for the
 * results list's scroll threshold. Before this, the hook fetched exactly one
 * `first: 20` page with no cursor tracking — an honest "379 MATCHES" caption
 * over a list that could never load row 21.
 * @module features/player-kanban/hooks/useAddPlayersSearch
 */

import { useCallback, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import {
  type PlayerSearchPlayerFragment,
  usePlayerSearchSearchPlayersQuery,
} from "@/generated/graphql";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";

/** Debounce before a keystroke reaches the network. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Minimum characters before the search fires. Three, not two: a `LIKE` pattern
 * under three characters defeats the backend's trigram index.
 */
export const MIN_PANEL_SEARCH_CHARS = 3;

/** Page size for the panel's search results. */
const PAGE_SIZE = 20;

/** Public API of {@link useAddPlayersSearch}. */
export interface UseAddPlayersSearchResult {
  /** Raw text in the field, echoed back for a controlled input. */
  readonly searchQuery: string;
  /** Update the field. */
  readonly onSearchChange: (text: string) => void;
  /** Clear the field and abandon any pending debounce. */
  readonly onSearchClear: () => void;
  /**
   * Whether search results — not the selected tab's body — own the panel body.
   * True as soon as the user has typed enough to search.
   */
  readonly isSearching: boolean;
  /** Matching players. */
  readonly players: readonly PlayerSearchPlayerFragment[];
  /**
   * The connection's total match count, for PC2's "ALL PLAYERS · N MATCHES"
   * caption. `null` while unknown (loading, or the backend omitted it).
   */
  readonly totalCount: number | null;
  /** Whether the first page of results is loading. */
  readonly isLoading: boolean;
  /** Whether a completed search matched nothing. */
  readonly isEmpty: boolean;
  /** Human-facing search error, or `null`. */
  readonly error: string | null;
  /** Fetch the next page, when there is one (SE-7100). */
  readonly loadMore: () => void;
  /** Whether more pages exist beyond the loaded rows (SE-7100). */
  readonly hasMore: boolean;
}

/**
 * Drive the panel's persistent global search field.
 * @param enabled - Whether the panel is mounted/expanded. A collapsed panel
 * must not hold an open search subscription.
 * @returns The {@link UseAddPlayersSearchResult} read model.
 */
const useAddPlayersSearch = (enabled: boolean): UseAddPlayersSearchResult => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearch(value);
  }, SEARCH_DEBOUNCE_MS);

  const onSearchChange = useCallback(
    (text: string) => {
      setSearchQuery(text);
      debouncedSetSearch(text);
    },
    [debouncedSetSearch]
  );

  const onSearchClear = useCallback(() => {
    debouncedSetSearch.cancel();
    setSearchQuery("");
    setDebouncedSearch("");
  }, [debouncedSetSearch]);

  const trimmed = debouncedSearch.trim();
  const canQuery = enabled && trimmed.length >= MIN_PANEL_SEARCH_CHARS;

  const { data, loading, error, fetchMore, networkStatus } =
    usePlayerSearchSearchPlayersQuery({
      variables: { query: trimmed, first: PAGE_SIZE },
      skip: !canQuery,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      notifyOnNetworkStatusChange: true,
    });

  /*
   * SE-7100: relay page-walk on scroll. The searchPlayers cache policy keys
   * by query/sort (not first/after), so fetched pages merge into one
   * connection and `players` below simply grows.
   */
  const { loadMore, hasMore } = useInfiniteScroll({
    pageInfo: data?.searchPlayers?.pageInfo,
    fetchMore,
    networkStatus,
  });

  const players = useMemo(
    () => (data?.searchPlayers?.edges ?? []).map(edge => edge.node),
    [data?.searchPlayers?.edges]
  );

  // Keyed off the RAW field, not the debounced value: the body must hand over
  // to search results the moment the user has typed enough, rather than
  // flashing the tab body for a debounce interval on every keystroke.
  const isSearching = searchQuery.trim().length >= MIN_PANEL_SEARCH_CHARS;

  const totalCount = data?.searchPlayers?.totalCount ?? null;

  return useMemo(
    () => ({
      searchQuery,
      onSearchChange,
      onSearchClear,
      isSearching,
      players,
      totalCount,
      isLoading: canQuery && loading && players.length === 0,
      isEmpty: canQuery && !loading && players.length === 0,
      error: error?.message ?? null,
      loadMore,
      hasMore,
    }),
    [
      canQuery,
      error?.message,
      hasMore,
      isSearching,
      loading,
      loadMore,
      onSearchChange,
      onSearchClear,
      players,
      searchQuery,
      totalCount,
    ]
  );
};

export default useAddPlayersSearch;
